const CACHE_KEY = "aqi_news_posts_manifest";
const POST_CACHE_PREFIX = "aqi_news_post:";

const apiBase = process.env.REACT_APP_BACKEND_URL || "";

const addImageVersion = (image, version) => {
    if(!image || !version) {
        return image || "";
    }

    return `${image}${image.includes("?") ? "&" : "?"}v=${encodeURIComponent(
        String(version),
    )}`;
};

const normalizeImageForReact = (image, version) => {
    const normalizedImage = image
        ? image
              .replace(/^\/api\/images\//, "/images/")
              .replace(/^\/uploads\//, `${apiBase}/uploads/`)
        : "";

    return addImageVersion(normalizedImage, version);
};

const normalizePostImages = (post) => ({
    ...post,
    image: normalizeImageForReact(post.image, post.version || post.updatedAt),
      blogImage: normalizeImageForReact(
        post.blogImage || post.image,
        post.version || post.updatedAt,
    ),
    content: Array.isArray(post.content)
        ? post.content.map((block) => ({
              ...block,
              image: normalizeImageForReact(
                  block.image,
                  post.version || post.updatedAt,
              ),
          }))
        : [],
});

const readCache = (key) => {
    try {
        const value = localStorage.getItem(key);
        return value ? JSON.parse(value) : null;
    } catch{
        return null;
    }
};

const writeCache = (key, value) => {
    localStorage.setItem(key, JSON.stringify(value));
};

export const clearNewsPostCache = () => {
    localStorage.removeItem(CACHE_KEY);

    Object.keys(localStorage)
        .filter((key) => key.startsWith(POST_CACHE_PREFIX))
        .forEach((key) => localStorage.removeItem(key));
};

export const getNewsPosts = async () => {
    const response = await fetch(`${apiBase}/api/news-posts`);
    if(!response.ok) {
        throw new Error("Unable to load news posts");
    }

    const manifest = await response.json();
    const cached = readCache(CACHE_KEY);

    if(cached?.version === manifest.version) {
        return {
            ...cached,
            posts: cached.posts.map(normalizePostImages),
        };
    }

    writeCache(CACHE_KEY, manifest);
    return {
        ...manifest,
        posts: manifest.posts.map(normalizePostImages),
    };
};

export const getNewsPost = async (idOrSlug) => {
    const manifest = await getNewsPosts();
    const summary = manifest.posts.find(
        (post) => String(post.id) === String(idOrSlug) || post.slug === idOrSlug,
    );
    const cacheKey = `${POST_CACHE_PREFIX}${idOrSlug}`;
    const cached = readCache(cacheKey);

    if(cached && summary && cached.version === summary.version) {
        return normalizePostImages(cached);
    }

    const response = await fetch(`${apiBase}/api/news-posts/${idOrSlug}`);
    if(!response.ok) {
        throw new Error("News post not found");
    }

    const post = await response.json();
    writeCache(`${POST_CACHE_PREFIX}${post.id}`, post);
    writeCache(`${POST_CACHE_PREFIX}${post.slug}`, post);
    return normalizePostImages(post);
};